import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosService from "../../../services/axiosServicesVisit";
import { API_URL, API } from "../../../config/api";

const apiUrlBase = `${API_URL}${API.API_PREFIX || '/api'}${API.VERSION || '/v1'}`;
const persons_api_base_url = `${apiUrlBase}${API.PERSONS?.BASE || "/persons"}`;
const delegates_api_base_url = `${apiUrlBase}${API.DELEGATES?.BASE || "/delegates"}`;

const buildQueryString = (params) => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0))
    .map(([key, value]) => {
      // Si la valeur est un tableau...
      if (Array.isArray(value)) {
        // ...on la transforme en une chaîne unique séparée par des virgules.
        // Exemple : [1, 2, 3] devient "key=1,2,3"
        // Ceci correspond à ce que le backend attend (`typeIds.split(',')`)
        return `${encodeURIComponent(key)}=${encodeURIComponent(value.join(','))}`;
      }
      // Sinon, on traite la valeur normalement.
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .filter(Boolean)
    .join('&');
};

export const getPersons = createAsyncThunk(
  "persons/getPersons",
  async ({ page = 1, limit = 10, search = '', type }, { rejectWithValue }) => {
    try {
      const queryParams = buildQueryString({ page, limit, search, type });
      const apiUrl = `${persons_api_base_url}?${queryParams}`;
      const response = await axiosService.get(apiUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getAssignedPersons = createAsyncThunk(
  "persons/getAssignedPersons",
  async ({ delegateId, page = 1, limit = 10, typeIds, filter = {} }, { rejectWithValue }) => {
    const queryParams = buildQueryString({
      page,
      limit,
      search: filter.search,
      typeIds: filter.type || typeIds || [1], 
      specialty: filter.specialty,
      government: filter.government,
      region: filter.region,
      visitStatus: filter.visitStatus,
      startDate: filter.startDate,
      endDate: filter.endDate,
       specialty: filter.specialty,
      government: filter.government,
      region: filter.region,
      _t: Date.now()
    });
    
    // === LA SEULE LIGNE QUI CHANGE VRAIMENT EST CI-DESSOUS ===

    // ANCIENNE URL (à supprimer ou commenter)
    // const apiUrl = `${persons_api_base_url}/delegate/${delegateId}?${queryParams}`;
    
    // NOUVELLE URL (plus logique et RESTful)
    const apiUrl = `${delegates_api_base_url}/${delegateId}/persons?${queryParams}`;
    
    try {
      const response = await axiosService.get(apiUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getNotAssignedPersons = createAsyncThunk(
  "persons/getNotAssignedPersons",
  async ({ delegateId, typeId, page, limit, search, governmentId, regionId, specialtyId }, { rejectWithValue }) => {
    try {
      const queryParams = buildQueryString({
        page,
        limit,
        search,
        governmentId,
        regionId,
        specialtyId,
      });
      
       const apiUrl = `${persons_api_base_url}/not-delegate/${delegateId}/${typeId}?${queryParams}`;
      
      const response = await axiosService.get(apiUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addPerson = createAsyncThunk(
  "persons/addPerson",
  async (personData, { rejectWithValue }) => {
    try {
      const response = await axiosService.post(persons_api_base_url, personData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePerson = createAsyncThunk(
  "persons/deletePerson",
  async (payload, { rejectWithValue }) => {
    try {
      const id = typeof payload === "object" ? payload.id : payload;
      const reason = typeof payload === "object" ? payload.reason : undefined;
      
      // ⚡ Utilisation de axiosService et persons_api_base_url
      const response = await axiosService.delete(`${persons_api_base_url}/${id}`, {
        params: { reason }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getPersonById = createAsyncThunk(
  "persons/getPersonById",
  async (personId, { rejectWithValue }) => {
    try {
      const response = await axiosService.get(`${persons_api_base_url}/${personId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getPersonByType = createAsyncThunk(
  "persons/getPersonByType",
  async ({ type, pageNumber = 1, limit = 10, filter = {} }, { rejectWithValue }) => {
    const queryParams = buildQueryString({ 
      page: pageNumber, limit, ...filter 
    });
    const apiUrl = `${persons_api_base_url}/type/${type}?${queryParams}`;
    try {
      const response = await axiosService.get(apiUrl);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updatePerson = createAsyncThunk(
  "persons/updatePerson",
  async ({ personId, personData }, { rejectWithValue }) => {
    try {
      const response = await axiosService.patch(`${persons_api_base_url}/${personId}`, personData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const filterPersons = createAsyncThunk(
  "persons/filterPersons",
  async (filterData, { rejectWithValue }) => {
    try {
      const response = await axiosService.post(`${persons_api_base_url}/filter`, filterData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const importPersons = createAsyncThunk(
  "persons/importPersons",
  async ({ file, personImportType = 'doctor' }, { rejectWithValue }) => {
    const apiUrl = `${persons_api_base_url}/import?type=${personImportType}`;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axiosService.post(apiUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const exportPersons = createAsyncThunk(
  "persons/exportPersons",
  async ({ type, format = 'csv', filter = {} }, { rejectWithValue }) => {
    try {
      let typeName;
      if (type === 1) {
        typeName = 'doctors';
      } else if (type === 2) {
        typeName = 'pharmacies';
      } else if (type === 3) { // --- AJOUT ---
        typeName = 'wholesalers';
      } else {
        return rejectWithValue("Type d'export non supporté.");
      }

      const queryParams = buildQueryString({ format, ...filter });
      const apiUrl = `${persons_api_base_url}/export/${typeName}?${queryParams}`;
      
      const response = await axiosService.get(apiUrl, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `export_${typeName}_${new Date().toISOString().split('T')[0]}.${format}`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      return { success: true };

    } catch (error) {
      const reader = new FileReader();
      let errorMessage = "Erreur de réseau ou de serveur.";
      if (error.response && error.response.data) {
        try {
          const errData = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(JSON.parse(reader.result));
            reader.onerror = () => reject(reader.error);
            reader.readAsText(error.response.data);
          });
          errorMessage = errData.message || JSON.stringify(errData);
        } catch (e) {
          errorMessage = "Impossible de lire le message d'erreur de l'API.";
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

const disassociatePersonsFromDelegate = createAsyncThunk(
  "persons/disassociatePersonsFromDelegate",
  async ({ delegateId, personIds }, { rejectWithValue }) => {
    try {
      const apiUrl = `${delegates_api_base_url}/${delegateId}/disassociate-persons`;
      const response = await axiosService.post(apiUrl, { personIds });
      return { delegateId, disassociatedPersonIds: personIds, backendMessage: response.data?.message };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export { disassociatePersonsFromDelegate as Unlinkdelegate };


/**
 * Récupère la liste des personnes archivées d'un certain type.
 */
export const getArchivedPersons = createAsyncThunk(
  "persons/getArchivedPersons",
  async ({ type, page, limit }, { rejectWithValue }) => {
    try {
      const queryParams = buildQueryString({ page, limit });
      const apiUrl = `${persons_api_base_url}/archive/type/${type}?${queryParams}`;
      const response = await axiosService.get(apiUrl);
      return response.data; // Attend { data: [...], total: ... }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Restaure une personne archivée.
 */
export const restorePerson = createAsyncThunk(
  "persons/restorePerson",
  async (personId, { rejectWithValue }) => {
    try {
      const apiUrl = `${persons_api_base_url}/${personId}/restore`;
      await axiosService.patch(apiUrl);
      return personId; // Renvoie l'ID en cas de succès
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const getAllDoctorsForFilter = createAsyncThunk(
  "persons/getAllDoctorsForFilter",
  async (_, { rejectWithValue }) => {
    try {
      // On appelle le nouvel endpoint "léger" qui n'a pas de limite de pagination
      const apiUrl = `${persons_api_base_url}/filter-list/doctors`;
      const response = await axiosService.get(apiUrl);
      
      // La réponse sera un tableau direct d'objets {id, firstname, lastname}
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getDoctorVisitHistory = createAsyncThunk(
  "persons/getDoctorVisits",
  async (doctorId, { rejectWithValue }) => {
    try {
      const response = await axiosService.get(`${apiUrlBase}/doctors/${doctorId}/visit-history`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);